import React from 'react';
import { Link } from 'react-router';
import { PageHero } from '../../components/PageHero';
import { Section } from '../../components/Section';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { IconWrap } from '../../components/IconWrap';
import useTheme from '../../hooks/useTheme';
import { megaGreenData } from '../../data/data';
import { CiTrash, CiFileOn, CiLock, CiBookmark } from 'react-icons/ci';
import { BiBoltCircle, BiBook, BiFile, BiShield, BiClipboard, BiBookAlt } from 'react-icons/bi';
import { HiOutlineDocumentText } from 'react-icons/hi';

export const Services: React.FC = () => {
    const { theme, breakpoint } = useTheme();
    const services = megaGreenData.services;

    const iconMap: Record<string, React.ReactNode> = {
        document: <CiFileOn />,
        Document: <HiOutlineDocumentText />,
        File: <BiFile />,
        shield: <BiShield />,
        Shield: <CiLock />,
        recycle: <CiTrash />,
        Recycle: <CiTrash />,
        bolt: <BiBoltCircle />,
        Bolt: <BiBoltCircle />,
        BoltCircle: <BiBoltCircle />,
        book: <BiBook />,
        Book: <CiBookmark />,
        GraduationCap: <BiBookAlt />,
        ClipboardCheck: <BiClipboard />,
    };

    return (
        <>
            <PageHero
                title="Services"
                subtitle={services.overviewIntro}
            />

            <Section>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: breakpoint.xs || breakpoint.sm ? '1fr' : breakpoint.md ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                        gap: '24px',
                    }}
                >
                    {services.services.map((service, idx) => {
                        const serviceColors: Array<'primary' | 'secondary' | 'accent' | 'highlight' | 'feature'> = [
                            'primary',
                            'secondary',
                            'accent',
                            'highlight',
                            'feature',
                        ];
                        const iconName = service.icon || 'document';
                        return (
                            <Card key={service.id} hoverable variant={idx % 2 === 0 ? 'primary' : 'secondary'}
                                style={{ display: 'flex', flexDirection: 'column' }}
                            >
                                <IconWrap
                                    icon={iconMap[iconName] || iconMap[iconName.charAt(0).toUpperCase() + iconName.slice(1)] || <CiFileOn />}
                                    size={32}
                                    color={serviceColors[idx] || 'primary'}
                                    withBackground={true}
                                    style={{ marginBottom: '16px' }}
                                />
                                <h3
                                    style={{
                                        fontSize: '20px',
                                        fontWeight: 600,
                                        color: theme.text.primary,
                                        marginBottom: '12px',
                                    }}
                                >
                                    {service.title}
                                </h3>
                                <p
                                    style={{
                                        fontSize: '14px',
                                        color: theme.text.muted,
                                        lineHeight: 1.5,
                                        marginBottom: '16px',
                                    }}
                                >
                                    {service.short}
                                </p>
                                <ul
                                    style={{
                                        listStyle: 'none',
                                        padding: 0,
                                        margin: '0 0 20px 0',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px',
                                    }}
                                >
                                    {service.bullets.map((bullet, idx) => (
                                        <li
                                            key={idx}
                                            style={{
                                                fontSize: '13px',
                                                color: theme.text.muted,
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '8px',
                                            }}
                                        >
                                            <span style={{ color: theme.palette.primary.main, marginTop: '4px', flexShrink: 0 }}>•</span>
                                            {bullet}
                                        </li>
                                    ))}
                                </ul>
                                <Link to={service.route} style={{ textDecoration: 'none', marginTop: 'auto' }}>
                                    <Button variant="secondary" style={{ width: '100%' }}>
                                        Explore
                                    </Button>
                                </Link>
                            </Card>
                        );
                    })}
                </div>
            </Section>

            <Section
                style={{
                    backgroundColor: theme.background.panel,
                }}
            >
                <h2
                    style={{
                        fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                        fontWeight: 700,
                        color: theme.text.primary,
                        marginBottom: '32px',
                        textAlign: 'center',
                    }}
                >
                    How to engage
                </h2>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: breakpoint.xs || breakpoint.sm ? '1fr' : 'repeat(3, 1fr)',
                        gap: '24px',
                        maxWidth: '900px',
                        margin: '0 auto',
                    }}
                >
                    {[
                        { title: 'Single study', desc: 'One-time assessment or consultation' },
                        { title: 'Bundle', desc: 'Multiple services packaged together' },
                        { title: 'Ongoing support', desc: 'Continuous monitoring and assistance' },
                    ].map((item, idx) => (
                        <Card key={idx} hoverable>
                            <h3
                                style={{
                                    fontSize: '18px',
                                    fontWeight: 600,
                                    color: theme.text.primary,
                                    marginBottom: '8px',
                                }}
                            >
                                {item.title}
                            </h3>
                            <p
                                style={{
                                    fontSize: '14px',
                                    color: theme.text.muted,
                                    lineHeight: 1.5,
                                }}
                            >
                                {item.desc}
                            </p>
                        </Card>
                    ))}
                </div>
            </Section>

            <Section
                style={{
                    backgroundColor: theme.background.panel,
                }}
            >
                <div
                    style={{
                        maxWidth: '800px',
                        margin: '0 auto',
                        textAlign: 'center',
                    }}
                >
                    <h2
                        style={{
                            fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                            fontWeight: 700,
                            color: theme.text.primary,
                            marginBottom: '16px',
                        }}
                    >
                        Tell us what you're planning
                    </h2>
                    <p
                        style={{
                            fontSize: '16px',
                            color: theme.text.muted,
                            marginBottom: '24px',
                        }}
                    >
                        {megaGreenData.contact.pageIntro}
                    </p>
                    <Link to="/contact" style={{ textDecoration: 'none' }}>
                        <Button variant="primary" style={{ padding: '12px 32px' }}>
                            Request a Consultation
                        </Button>
                    </Link>
                </div>
            </Section>
        </>
    );
};

